import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DahliasShell from "../components/dahlias/DahliasShell";
import PatternBand from "../components/dahlias/PatternBand";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { DahliasLogo } from "../components/brand/Logos";
import { Mail, MapPin, Heart, Send } from "lucide-react";
import CmsText from "../components/CmsText";
import { useSiteImage, useSiteText } from "../store/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NANA_JEAN_FALLBACK = "/photos/nana-jean.jpg";
const MUMS_SHED_FALLBACK = "/photos/mums-shed.jpg";

export default function DahliasAbout() {
  const nanaImg = useSiteImage("about.nana_jean.image", NANA_JEAN_FALLBACK);
  const shedImg = useSiteImage("about.mums_shed.image", MUMS_SHED_FALLBACK);
  const heading = useSiteText("about.heading", "Our farm, growing on\n*Nana Jean's legacy.*");
  const body = useSiteText(
    "about.body",
    "Heritage Dahlias is a small family flower business growing on in honour of our Nana Jean, born *1932*. We inherited her green fingers, and the farm is the place we keep her stories alive.\n\nEverything we do is on a small scale and by hand: a few rows of dahlias, a season of workshops, and tubers shared a few hundred at a time. If you've ever stood in a flower field with someone you love, you already know why we do this."
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/contact`, { name, email, message });
      toast.success("Thank you, we'll write back soon.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error("Couldn't send your message just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DahliasShell>
      {/* Top pattern band, sets the bright dahlia mood */}
      <PatternBand pattern="rich" variant="tall" tint={0.3} testid="about-band-top" />

      {/* In Loving Memory , feature banner above Nana Jean's photo */}
      <section
        data-testid="about-in-loving-memory"
        className="max-w-3xl mx-auto px-5 md:px-8 pt-12 md:pt-20 text-center"
      >
        <span className="block font-label tracking-[0.55em] text-[12px] md:text-[13px] text-[var(--berry)]">
          ✦ IN LOVING MEMORY ✦
        </span>
        <div className="mt-4 mx-auto h-px w-24 bg-[var(--berry)]/40" />
        <p className="mt-5 font-display-italic text-2xl md:text-3xl text-[var(--dahlias-forest)] leading-snug">
          For our Nana Jean , who planted the first row, and the love that grew into everything else.
        </p>
      </section>

      {/* About section, Nana Jean portrait + story */}
      <section
        data-testid="about-page"
        className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
      >
        {/* Nana Jean portrait, warm berry frame */}
        <div className="lg:col-span-6 relative">
          <div className="relative">
            <div className="absolute inset-0 wobble-a border-[3px] border-[var(--berry)]/40 -translate-x-3 -translate-y-3" />
            <div className="absolute inset-0 wobble-a border-[3px] border-[var(--dahlias-clay)]/35 translate-x-3 translate-y-3" />
            <div
              data-testid="nana-jean-photo"
              className="relative aspect-[3/4] bg-cover bg-center bg-[var(--dahlias-paper)] wobble-a border-[3px] border-[var(--berry)]"
              style={{ backgroundImage: `url(${nanaImg})` }}
              role="img"
              aria-label="Nana Jean"
            />
          </div>

          {/* Caption plaque (without the IN LOVING MEMORY eyebrow , it now lives at the top) */}
          <div className="mt-7 text-center">
            <h2 className="font-display-italic text-3xl md:text-4xl text-[var(--dahlias-forest)]">
              Nana Jean
            </h2>
            <span className="block mt-1 text-sm text-[var(--dahlias-ink)]/65 tracking-wider">
              Born 1932
            </span>
          </div>

          {/* Floating sage badge */}
          <div className="absolute -top-7 -right-3 md:-top-10 md:-right-10 bg-[var(--dahlias-cream)] wobble-blob p-3 md:p-4 shadow-xl border-[3px] border-[var(--berry-bright)]/50">
            <DahliasLogo variant="sage" className="w-20 h-20 md:w-28 md:h-28" />
          </div>
        </div>

        {/* Story */}
        <div className="lg:col-span-6">
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--berry)]">
            ✦ ABOUT US ✦
          </span>
          <h1
            data-testid="about-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--dahlias-forest)] mt-4 leading-[1.05] whitespace-pre-line"
          >
            <CmsText text={heading} italicClass="font-display-italic text-[var(--dahlias-sage-deep)]" />
          </h1>
          <div className="mt-7 space-y-5 text-xl md:text-2xl text-[var(--dahlias-ink)]/85 leading-relaxed max-w-xl">
            {body.split(/\n\s*\n/).map((para, i) => (
              <p key={i} className="whitespace-pre-line">
                <CmsText text={para} italicClass="font-display-italic text-[var(--dahlias-sage-deep)]" />
              </p>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3 text-[var(--dahlias-forest)]">
            <Heart className="w-5 h-5 text-[var(--dahlias-clay)] fill-[var(--dahlias-clay)]" />
            <span className="font-display text-xl italic">For Nana Jean.</span>
          </div>

          {/* Mum's shed detail, wobbly card */}
          <div className="mt-10 grid grid-cols-5 gap-4 items-center bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 p-4 md:p-5 wobble-b">
            <div
              data-testid="mums-shed-photo"
              className="col-span-2 aspect-[4/3] bg-cover bg-center wobble-c"
              style={{ backgroundImage: `url(${shedImg})` }}
              role="img"
              aria-label="Mum's Shed"
            />
            <div className="col-span-3">
              <span className="font-label tracking-[0.35em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">
                ✦ WHERE IT ALL HAPPENS ✦
              </span>
              <p className="mt-2 font-display-italic text-xl md:text-2xl text-[var(--dahlias-forest)] leading-snug">
                Mum's Shed.
              </p>
              <p className="mt-1 text-sm md:text-base text-[var(--dahlias-ink)]/70 leading-relaxed">
                The little red shed at the heart of the farm. Every tuber and bunch passes through here.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PatternBand pattern={1} variant="thin" tint={0.3} testid="about-band-1" />

      {/* Mystery Box origin story */}
      <section
        data-testid="origin-story"
        className="bg-[var(--dahlias-paper)] border-y-[3px] border-[var(--berry-bright)]/45"
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--berry)]">
            ✦ HOW IT STARTED ✦
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-[var(--dahlias-forest)] leading-[1.05]">
            It started with the
            <span className="font-display-italic text-[var(--berry)]"> Mystery Boxes.</span>
          </h2>
          <div className="mt-6 space-y-5 text-lg md:text-xl text-[var(--dahlias-ink)]/85 leading-relaxed max-w-2xl mx-auto text-left md:text-center">
            <p>
              The first thing we ever sold was a Mystery Box. The idea was simple:
              quality tubers, in a bundle, at a price that meant anyone could fill
              their garden with proper dahlias without the eye-watering single-variety price tag.
            </p>
            <p>
              That's still how we think about it. The named varieties are a treat.
              The mystery boxes are how a garden becomes a flower farm.
            </p>
          </div>
        </div>
      </section>

      <PatternBand pattern={3} variant="thin" tint={0.3} testid="about-band-2" />

      {/* 2025 Album */}
      <Album />

      {/* Scalloped sage transition */}
      <div aria-hidden="true" className="h-7 bg-[var(--dahlias-sage)] scallop-bottom" />

      {/* Contact, sage band */}
      <section className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-cream)]/80">
              ✦ GET IN TOUCH ✦
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--dahlias-cream)] mt-4 leading-[1.05]">
              Say <span className="font-display-italic">hello.</span>
            </h2>
          <p className="mt-6 text-lg md:text-xl text-[var(--dahlias-cream)]/90 leading-relaxed">
              Questions about tubers, workshops, the app trial, or just want to say kia ora?
              We read every message.
            </p>
            <div className="mt-8 space-y-4 text-base">
              <a
                href="mailto:heritagedahliasnz@gmail.com"
                className="flex items-center gap-3 text-[var(--dahlias-cream)] hover:text-white transition"
                data-testid="about-email-link"
              >
                <span className="w-10 h-10 wobble-blob bg-[var(--dahlias-cream)]/15 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                heritagedahliasnz@gmail.com
              </a>
              <p className="flex items-center gap-3 text-[var(--dahlias-cream)]/90">
                <span className="w-10 h-10 wobble-blob bg-[var(--dahlias-cream)]/15 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                Aotearoa
              </p>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5 bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] p-6 md:p-8 wobble-a border-2 border-[var(--dahlias-cream)]/40"
          >
            <div>
              <Label
                htmlFor="c-name"
                className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]"
              >
                Your name
              </Label>
              <Input
                id="c-name"
                data-testid="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="First & last"
                className="mt-2 h-13 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
              />
            </div>
            <div>
              <Label
                htmlFor="c-email"
                className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]"
              >
                Email
              </Label>
              <Input
                id="c-email"
                data-testid="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@somewhere.nz"
                className="mt-2 h-13 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
              />
            </div>
            <div className="md:col-span-2">
              <Label
                htmlFor="c-msg"
                className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]"
              >
                Message
              </Label>
              <Textarea
                id="c-msg"
                data-testid="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                required
                placeholder="Tell us what's on your mind…"
                className="mt-2 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-a"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="h-13 px-8 text-base bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md inline-flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {busy ? "Sending…" : "Send message"}
              </Button>
            </div>
          </form>
        </div>

        {/* Tribute to Nana Jean */}
        <div className="max-w-3xl mx-auto px-5 md:px-8 pb-14 md:pb-20 text-center">
          <div
            data-testid="nana-jean-tribute"
            className="bg-[var(--dahlias-cream)]/12 border-2 border-[var(--dahlias-cream)]/25 wobble-a px-6 md:px-10 py-10 md:py-12"
          >
            <Heart className="w-7 h-7 mx-auto text-[var(--dahlias-clay)] fill-[var(--dahlias-clay)]" />
            <p className="mt-4 font-display-italic text-2xl md:text-3xl text-[var(--dahlias-cream)] leading-snug">
              "Thank you, Nana Jean."
            </p>
            <p className="mt-4 text-[var(--dahlias-cream)]/85 leading-relaxed max-w-xl mx-auto text-base md:text-lg">
              For the love of flowers, for the patience to teach it, and for the
              quiet way you've stayed with us in every row we plant. This whole
              farm is for you.
            </p>
            <p className="mt-5 font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-cream)]/75">
              ✦ EST · 1932 ✦
            </p>
          </div>
        </div>
      </section>

      <div aria-hidden="true" className="h-7 bg-[var(--dahlias-sage)] scallop-top" />
    </DahliasShell>
  );
}

/* ----- 2025 Album ----- */
const ALBUM_KEYS = [
  "about.album.2025.1",
  "about.album.2025.2",
  "about.album.2025.3",
  "about.album.2025.4",
  "about.album.2025.5",
  "about.album.2025.6",
];
const ALBUM_FALLBACKS = [
  "/photos/dahlia-field.jpg",
  "/photos/cut-dahlias-spread.jpg",
  "/photos/open-pink-dahlias.jpg",
  "/photos/pink-dahlia-sky.jpg",
  "/photos/mums-shed.jpg",
  "/photos/dahlia-field.jpg",
];

function Album() {
  const photos = ALBUM_KEYS.map((k, i) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSiteImage(k, ALBUM_FALLBACKS[i])
  );
  const radii = ["wobble-a", "wobble-b", "wobble-c"];
  return (
    <section
      data-testid="album-2025"
      className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20"
    >
      <div className="text-center mb-10 md:mb-14">
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
          ✦ ALBUM ✦
        </span>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-[var(--dahlias-forest)] leading-[1.05]">
          The 2025 <span className="font-display-italic text-[var(--dahlias-clay)]">season.</span>
        </h2>
        <p className="mt-4 text-base md:text-lg text-[var(--dahlias-ink)]/75 max-w-2xl mx-auto">
          A handful of moments from the rows, the shed, and the picking baskets.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {photos.map((src, i) => {
          const colours = ["var(--berry)", "var(--berry-bright)", "var(--dahlias-clay)", "var(--berry-peach)", "var(--berry-deep)", "var(--berry)"];
          return (
            <div
              key={i}
              data-testid={`album-photo-${i}`}
              className={`aspect-[4/5] bg-cover bg-center bg-[var(--dahlias-sage)]/10 border-[3px] ${radii[i % radii.length]}`}
              style={{
                ...(src ? { backgroundImage: `url(${src})` } : {}),
                borderColor: colours[i % colours.length],
              }}
              role="img"
              aria-label={`Heritage Dahlias 2025 photo ${i + 1}`}
            />
          );
        })}
      </div>
    </section>
  );
}
